import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { IonModal, IonIcon, IonButton, IonToggle } from "@ionic/react";
import { close } from "ionicons/icons";
import NumberSelector from "../NumberSelector";
import TherapyDurationSlider from "../MinuteGoals";
import StepsSlider from "../Sliders/CountSlider";
import Counter from "../../ui/Counter";
import { BiwazeEnum } from "../../../utils/constants";

interface BiWazeGoalModalProps {
  onClose: () => void;
  deviceName?: string;
  goalData?: any;
  onChange?: (updatedGoal: any) => void;
  onSave?: (updatedGoal: any) => void;
  showNumbers?: boolean;
  showMinutes?: boolean;
  showSteps?: boolean;
  showSleepGoal?: boolean;
  toggleTitle?: string;
  subTitle?: string;
  subText?: string;
  noOfTimes?: string | React.ReactElement;
  position?: "middle" | "bottom";
  showToggle?: boolean;
  showRemove?: boolean;
  showCounter?: boolean;
  onRemoveGoal?: (updatedGoal: any) => void;
  autoPresentOnMount?: boolean;
}

export interface BiWazeGoalModalRef {
  present: () => void;
  dismiss: () => void;
}

const BiWazeGoalModal = forwardRef<BiWazeGoalModalRef, BiWazeGoalModalProps>(({
  onClose,
  deviceName = "BiWaze® Cough",
  goalData = {},
  onChange,
  onSave,
  onRemoveGoal,
  showNumbers = true,
  showMinutes = false,
  showSteps = false,
  showSleepGoal = false,
  toggleTitle = "Track goal",
  subTitle,
  subText = "",
  noOfTimes = showMinutes || showSteps ? "Steps per day" : "Times per day",
  position = "middle",
  showToggle = true,
  showRemove = true,
  showCounter = false,
  autoPresentOnMount = false,
}, ref) => {
  const modal = useRef<HTMLIonModalElement>(null);
  const [trackGoal, setTrackGoal] = useState(Boolean(goalData.isActive));
  const [timesPerDay, setTimesPerDay] = useState(goalData.target_count || 3);
  const [therapyMinutes, setTherapyMinutes] = useState(
    goalData.target_seconds !== undefined ? goalData.target_seconds / 60 : 0
  );
  const [sleepHours, setSleepHours] = useState(goalData.target_hours || 8);

  // Expose present and dismiss methods to parent
  useImperativeHandle(ref, () => ({
    present: () => modal.current?.present(),
    dismiss: () => modal.current?.dismiss(),
  }));

  // Auto-present modal on mount if autoPresentOnMount is true
  useEffect(() => {
    if (autoPresentOnMount) {
      modal.current?.present();
    }
  }, [autoPresentOnMount]);

  useEffect(() => {
    setTrackGoal(Boolean(goalData?.isActive));
    setTimesPerDay(goalData?.target_count);
    setTherapyMinutes(
      goalData.target_seconds !== undefined ? goalData.target_seconds / 60 : 0
    );
    setSleepHours(goalData?.target_hours);
  }, [goalData]);

  const handleSave = () => {
    let updatedGoal;
    if (
      goalData.isActive &&
      goalData.goal_type === "biwaze_clear" &&
      therapyMinutes === undefined
    ) {
      updatedGoal = {
        ...goalData,
        isActive: trackGoal,
        target_count: timesPerDay,
        target_seconds: 30 * 60,
        target_hours: sleepHours,
      };
    } else if (
      goalData.isActive &&
      goalData.goal_type === "step_tracking" &&
      timesPerDay === undefined
    ) {
      updatedGoal = {
        ...goalData,
        isActive: trackGoal,
        target_count: 7500,
        target_seconds: undefined,
        target_hours: sleepHours,
      };
    } else {
      updatedGoal = {
        ...goalData,
        isActive: trackGoal,
        target_count: timesPerDay,
        target_seconds:
          therapyMinutes !== undefined ? therapyMinutes * 60 : undefined,
        target_hours: sleepHours,
      };
    }
    onSave?.(updatedGoal);
    modal.current?.dismiss();
  };

  const handleRemoveGoal = () => {
    const updatedGoal = {
      ...goalData,
      isActive: false,
      effective_to: new Date().toLocaleDateString("en-CA"), // YYYY-MM-DD format in local time
    };
    onRemoveGoal?.(updatedGoal);
    modal.current?.dismiss();
  };

  const handleChange = (field: string, value: any) => {
    const updatedGoal = { ...goalData, [field]: value };
    if (field === "target_seconds" && value !== undefined) {
      updatedGoal.target_seconds = value * 60;
    }
    if (field === "target_count" && value !== undefined) {
      // console.log("Target Count Changed:", value);
      if (value === 0) {
        updatedGoal.isActive = false;
        updatedGoal.target_count = timesPerDay;
      } else {
        updatedGoal.target_count = value;
        setTimesPerDay(value);
        updatedGoal.isActive = true;
        setTrackGoal(true);
      }
    }
    if (field === "isActive" && value) {
      updatedGoal.isActive = true;
      updatedGoal.target_count = showSteps
        ? 1000
        : showNumbers || showCounter
        ? 1
        : updatedGoal.target_count;
      updatedGoal.target_seconds = showMinutes
        ? 10 * 60
        : updatedGoal.target_seconds;
      updatedGoal.target_hours = showSleepGoal ? 5 : updatedGoal.target_hours;
    }
    onChange?.(updatedGoal);
  };

  return (
    <IonModal
      ref={modal}
      onWillDismiss={onClose}
      canDismiss={true}
      showBackdrop={true}
      backdropDismiss={true}
      style={{
        "--background": "transparent",
        // '--background': 'rgba(0,0,0,0.4)', // darker translucent background
        "--box-shadow": "none",
        "--border-radius": "20px",
        // "backdrop-filter": "blur(1px)",
        // marginBottom: "10px"
      }}
    >
      {/* </IonModal>
    <IonModal isOpen={isOpen} onDidDismiss={onClose} > */}
      <div
        className="md:max-h-[90vh]"
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "white",
          borderRadius: "20px",
          // padding: '24px',
          // boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
          maxHeight: "70vh",
          overflow: "hidden",
          // paddingBottom: '20px',
          margin: "20px",
        }}
      >
        <div className="flex flex-col items-center p-4 h-full gap-5">
          <div className="w-full flex justify-between items-center mx-4 relative">
            <h2 className="text-2xl font-bold text-center flex-grow">
              {deviceName}
            </h2>
            <button
              onClick={() => modal.current?.dismiss()}
              className="text-gray-400 absolute -top-1 right-1"
            >
              <IonIcon icon={close} style={{ fontSize: "26px" }} />
            </button>
          </div>

          {showToggle && (
            <div className="w-full bg-gray-100 rounded-xl flex h-[53px] justify-between items-center px-4 py-2 mb-2">
              <span className="text-[18px] font-medium">{toggleTitle}</span>
              <IonToggle
                checked={trackGoal}
                onIonChange={(e) => {
                  setTrackGoal(e.detail.checked);
                  handleChange("isActive", e.detail.checked);
                }}
              />
            </div>
          )}

          <div
            className="flex flex-col items-center"
            style={{ paddingTop: showSteps ? "5px" : "10px" }}
          >
            {showNumbers && !showSteps && (
              <NumberSelector
                size={46}
                gap={15}
                subtitle={subTitle}
                defaultValue={timesPerDay}
                onChange={(value) => {
                  setTimesPerDay(value);
                  handleChange("target_count", value);
                }}
              />
            )}
            {deviceName === BiwazeEnum.CLEAR && (
              <div className="flex flex-row justify-center items-center py-[5px] px-[15px] h-[30px]  rounded-[40px]">
                <span className="text-md">Times per day</span>
              </div>
            )}

            {showMinutes && (
              <TherapyDurationSlider
                defaultValue={therapyMinutes}
                onChange={(value) => {
                  setTherapyMinutes(value);
                  handleChange("target_seconds", value);
                }}
              />
            )}

            {showSteps && (
              <StepsSlider
                defaultValue={timesPerDay}
                onChange={(value) => {
                  value !== 0 && setTimesPerDay(value);
                  handleChange("target_count", value);
                }}
              />
            )}
            {showCounter && (
              <Counter
                min={5}
                max={50}
                initialValue={goalData.target_count}
                onChange={(value: number) => {
                  setTimesPerDay(value);
                  handleChange("target_count", value);
                }}
                size={46}
                gap={15}
              />
            )}

            {showSleepGoal && (
              <NumberSelector
                values={[5, 6, 7, 8, 9]}
                size={46}
                gap={15}
                defaultValue={sleepHours}
                onChange={(value) => {
                  setSleepHours(value);
                  handleChange("target_hours", value);
                }}
              />
            )}
            <div style={{ paddingTop: showSteps ? "10px" : "10px" }}></div>

            <div className="flex flex-row justify-center items-center py-[5px] px-[15px] h-[30px]  rounded-[40px]">
              <span className="text-md">
                {noOfTimes
                  ? noOfTimes
                  : showCounter
                  ? "cmH₂O"
                  : showSleepGoal
                  ? "hours per night"
                  : showMinutes
                  ? "Minutes per day"
                  : showSteps
                  ? "Steps per day"
                  : "Times per day"}
              </span>
            </div>
            {subText && <p className="mt-2 text-center">{subText}</p>}
          </div>

          <div className="w-full flex flex-col items-center gap-5 mb-4">
            <IonButton
              expand="block"
              onClick={handleSave}
              className="primary-button"
              style={{
                "--border-radius": "24px",
                "--height": "46px",
                margin: "12px",
                padding: "12px 20px",
                fontSize: "22px",
              }}
            >
              Save
            </IonButton>

            {showRemove && (
              <button
                onClick={handleRemoveGoal}
                className="secondary-button mt-2"
                style={{ color: "red" }}
              >
                Remove Goal
              </button>
            )}
          </div>
        </div>
      </div>
    </IonModal>
  );
});

BiWazeGoalModal.displayName = 'BiWazeGoalModal';

export default BiWazeGoalModal;
